import { registerActions } from "../actionRegistry";
import { getDomElement, setHtml, fadeIn, fadeOut, h } from "../utils/dom";
import { escapeHtml } from "../utils/escapeHtml";
import { modal } from "./modal";

export function showLoadingOverlay(message: string): void {
  const msgEl = getDomElement("nloader-msg");
  const backEl = getDomElement("nloader-back");
  const frontEl = getDomElement("nloader-front");

  if (msgEl) {
    setHtml(msgEl, message);
  }
  if (backEl) {
    fadeIn(backEl);
  }
  if (frontEl) {
    fadeIn(frontEl);
  }
}

export function hideLoadingOverlay(): void {
  const backEl = getDomElement("nloader-back");
  const frontEl = getDomElement("nloader-front");

  if (backEl) {
    fadeOut(backEl);
  }
  if (frontEl) {
    fadeOut(frontEl);
  }
}

export const nloaderstop = hideLoadingOverlay;

export type MessageType = "success" | "error" | "info";

/**
 * Legacy toast renderer, kept for the `data-init-qmsg` directive and window-registered
 * inline callers. New code uses toastSuccess/toastError/toastInfo below.
 */
export function qmsg(msg: string, duration = 5000, icon?: MessageType): void {
  const msgId = Math.floor(Math.random() * 10000000000);

  // Always use 5000ms as per legacy code
  duration = 5000;

  // Escape here rather than at the ~60 call sites. Most of them pass
  // `(err as Error).message` straight from a tRPC error, and several server
  // messages embed user input verbatim (e.g. `${filename}: only jpg … allowed`),
  // so this string reaches `insertAdjacentHTML` carrying attacker-influenced
  // text. Toasts are plain text by design — nothing renders markup through here.
  const safeMsg = escapeHtml(msg);

  let msgHtml: string;

  if (icon === "success" || icon === "error") {
    let iconHtml: string;
    let animationClass: string;

    if (icon === "success") {
      iconHtml =
        '<div class="qmsg-inner-icon"><span style="color: #25BF0F;"><i class="fas fa-check-circle"></i></span></div>';
      animationClass = "zoomIn";
    } else {
      iconHtml =
        '<div class="qmsg-inner-icon"><span style="color: #BF0F0F;"><i class="fas fa-exclamation-circle"></i></span></div>';
      animationClass = "headShake";
    }

    msgHtml = `<div class="qmsg-item animated ${animationClass} faster" id="qmsg-${msgId}">
      <div class="qmsg-inner">
        <table cellpadding="0" cellspacing="0" width="100%">
          <tr>
            <td>${iconHtml}</td>
            <td><div class="qmsg-inner-text">${safeMsg}</div></td>
          </tr>
        </table>
      </div>
    </div>`;
  } else {
    msgHtml = `<div class="qmsg-item animated zoomIn faster" id="qmsg-${msgId}">
      <div class="qmsg-inner">${safeMsg}</div>
    </div>`;
  }

  const msgContainer = getDomElement("msg");
  if (msgContainer) {
    msgContainer.insertAdjacentHTML("afterbegin", msgHtml);
  }

  // Auto-hide after duration
  setTimeout(() => {
    qmsghide(String(msgId));
  }, duration);
}

/** Green-check success toast. */
export function toastSuccess(msg: string): void {
  qmsg(msg, 5000, "success");
}

/** Red-exclamation error toast. */
export function toastError(msg: string): void {
  qmsg(msg, 5000, "error");
}

/** Plain informational toast (no icon). */
export function toastInfo(msg: string): void {
  qmsg(msg, 5000);
}

/**
 * A toast that navigates or opens a modal when clicked.
 *
 * Everything about the old shape existed to survive a round-trip through
 * `innerHTML` that never needed to happen: a `Math.random()` id was minted so
 * the element could be found again, the target was written into
 * `data-msg-modal` / `data-msg-href` and read straight back out with
 * `getAttribute`, and both had to be `escapeHtml`-ed because they were being
 * interpolated into quoted attributes. Building the node keeps the reference, so
 * the id, the attributes and the escaping all stop being necessary — the handler
 * closes over `targetModal` / `targetUrl` directly.
 *
 * `_duration` is unused and always has been: these toasts stay until clicked.
 */
export function qmsglink(
  msg: string,
  _duration: number,
  icon: MessageType | undefined,
  targetModal: string,
  targetUrl: string
): void {
  const msgContainer = getDomElement("msg");
  if (!msgContainer) return;

  const animationClass = icon === "error" ? "headShake" : "zoomIn";
  let inner: HTMLElement;

  if (icon === "success" || icon === "error") {
    const glyph = h(
      "span",
      "",
      h("i", `fas fa-${icon === "success" ? "check" : "exclamation"}-circle`)
    );
    glyph.style.color = icon === "success" ? "#25BF0F" : "#BF0F0F";

    const table = h("table");
    table.setAttribute("cellpadding", "0");
    table.setAttribute("cellspacing", "0");
    table.setAttribute("width", "100%");
    table.append(
      h(
        "tr",
        "",
        h("td", "", h("div", "qmsg-inner-icon", glyph)),
        h("td", "", h("div", "qmsg-inner-text", msg))
      )
    );
    inner = h("div", "qmsg-inner", table);
  } else {
    inner = h("div", "qmsg-inner", msg);
  }

  const item = h("div", `qmsg-item animated ${animationClass} faster qmsg-clickable`, inner);
  if (targetModal) {
    item.addEventListener("click", () => {
      modal(targetModal, "large");
    });
  } else if (targetUrl) {
    item.addEventListener("click", () => {
      location.href = targetUrl;
    });
  }

  msgContainer.prepend(item);
}

export function qmsghide(id: string): void {
  const msgEl = getDomElement(`qmsg-${id}`);
  if (msgEl) {
    fadeOut(msgEl);
  }
}

/**
 * Run an api call with standard toast feedback: error toast on failure, optional success
 * toast. Returns the result, or null when the call threw (tRPC results are never null, so
 * a null return is a reliable failure signal for follow-up UI work).
 */
export async function withToast<T>(fn: () => Promise<T>, successMsg?: string): Promise<T | null> {
  try {
    const result = await fn();
    if (successMsg) toastSuccess(successMsg);
    return result;
  } catch (err: unknown) {
    toastError(err instanceof Error ? err.message : "Something went wrong");
    return null;
  }
}

registerActions({
  nloader: showLoadingOverlay,
  nloaderstop: hideLoadingOverlay,
  qmsg,
  qmsghide,
});
